import { useCallback, useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { getMonthToDateRange } from '@/utils/date';
import { DATE_RANGE_FILTER_KEYS } from '@/utils/filterFields';
import { toPeriodFilterValues, toPeriodSearch } from '@/utils/filters';
import type { WebUrlListParams } from '../types/webUrl.types';

const route = getRouteApi('/_auth/settings/web-urls');

export function useWebUrlFilters() {
  const { dateFrom, dateTo, q: appliedSearchText } = route.useSearch();
  const navigate = route.useNavigate();
  const defaultValues = useMemo(() => getMonthToDateRange(), []);

  const appliedValues = useMemo(
    () => toPeriodFilterValues({ dateFrom, dateTo }, defaultValues),
    [dateFrom, dateTo, defaultValues],
  );

  const params = useMemo(
    (): WebUrlListParams => ({
      activeFrom: appliedValues.dateFrom,
      activeTo: appliedValues.dateTo,
    }),
    [appliedValues],
  );

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    defaultValues,
    rangeKeys: DATE_RANGE_FILTER_KEYS,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toPeriodSearch(values) }),
        replace: true,
      });
    },
  });

  const applySearchText = useCallback(
    (text: string | undefined) => {
      void navigate({ search: (previous) => ({ ...previous, q: text }), replace: true });
    },
    [navigate],
  );

  return { ...filters, params, appliedSearchText, applySearchText };
}
